import { useState, useSyncExternalStore } from 'react';

import { useKeyHint } from '../lib/shortcuts/index.js';
import { undoService, type HistorySource } from '../lib/undo.js';
import { useWorkspace } from '../state/workspace.js';
import { EmptyState, IconButton, SelectField } from './ui/controls.js';
import { Icon } from './ui/icons.js';

function useUndoVersion(): number {
  return useSyncExternalStore(
    (fn) => undoService.subscribe(fn),
    () => undoService.version,
  );
}

function timeOf(ms: number | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Undo History (Blender's Edit > Undo History, Unity's Undo History window):
 * every step of the active editor's history; click one to jump there.
 */
export function HistoryPanel() {
  useUndoVersion();
  const view = useWorkspace((s) => s.view);
  const [pinned, setPinned] = useState<string | null>(null);
  const undoKey = useKeyHint('edit.undo');
  const redoKey = useKeyHint('edit.redo');

  const sources = undoService.list();
  const source: HistorySource | null = (pinned ? undoService.get(pinned) : null) ?? undoService.active(view) ?? sources[0] ?? null;

  if (!source) {
    return (
      <div className="history-panel">
        <EmptyState icon="undo" title="Nothing to undo yet">
          Edits in the Scene, Model, Pixel, Sound, Animator, UI and Mixer editors appear here.
        </EmptyState>
      </div>
    );
  }

  const { past, future } = source.snapshot();
  const steps = [{ label: 'Original', time: undefined as number | undefined }, ...past, ...future];

  return (
    <div className="history-panel">
      <div className="history-head">
        <SelectField
          value={source.id}
          options={sources.map((s) => ({ value: s.id, label: s.label }))}
          onChange={(id) => setPinned(id === undoService.active(view)?.id ? null : id)}
        />
        <IconButton icon="undo" title="Undo" shortcut={undoKey} disabled={past.length === 0} onClick={() => undoService.jump(source.id, past.length - 1)} />
        <IconButton icon="redo" title="Redo" shortcut={redoKey} disabled={future.length === 0} onClick={() => undoService.jump(source.id, past.length + 1)} />
      </div>
      <ol className="history-list">
        {steps.map((step, index) => {
          const current = index === past.length;
          const undone = index > past.length;
          return (
            <li key={index}>
              <button
                type="button"
                className={`history-step${current ? ' is-current' : ''}${undone ? ' is-undone' : ''}`}
                title={current ? 'Current state' : `Jump to "${step.label}"`}
                onClick={() => undoService.jump(source.id, index)}
              >
                <span className="history-mark">{current ? <Icon name="chevronRight" size={12} /> : index}</span>
                <span className="history-label">{step.label}</span>
                <span className="history-time">{timeOf(step.time)}</span>
              </button>
            </li>
          );
        })}
      </ol>
      <div className="history-foot muted">
        {past.length} step{past.length === 1 ? '' : 's'} · {future.length} undone
      </div>
    </div>
  );
}
